import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SubtitleDisplay from '../../components/common/SubtitleDisplay';
import ScientistNote from '../../components/common/ScientistNote';
import InventoryBox from '../../components/common/InventoryBox';
import GameHeader from '../../components/common/GameHeader';
import HintPanel from '../../components/common/HintPanel';
import ExplanationModal from '../../components/common/ExplanationModal';
import MissionTransition from './MissionTransition';
import { evaluate, initialContext, nextSteps, perform, resolveSlot, slotKey, status } from './simEngine';
import { CLOSEUPS, CONTROLS, Captioned, HoldButton, ItemExtras, REACTIONS, badgesFor, controlOf, howTo as howToFor, howToAct, nudge as nudgeFor, shownName } from './mechanics';
import { SHIFT_SECONDS, clockText, deadlineSeconds, forgiveSlip, groupLost, groupSolved, pace, rearm, shiftPlan, startMinutes } from './shiftEngine';
import './MissionGame.css';

/**
 * The mission template player - one fixed component that plays any simulation mission built by
 * backend services/game_builder/game.py (no generated code). The world is a set of items with state;
 * actions change that state through effects, events fire when conditions become true, and the
 * mission ends on `goal` or `fail` - all evaluated by simEngine.js, the twin of the backend solver.
 *
 * There are NO action buttons: every action is performed ON its item, by its `interaction`:
 *   drag    - (a 'move' in the data - nothing is actually dragged; see "Moving things" below)
 *   write   - open the record, put the thing the entry is about on the new line, sign with ✍️
 *             (several writes on one record are told apart by their `about` item)
 *   hold    - open the item, press and hold      switch - open the item, flip its switch
 *   gesture - click the person, use ☝️            exit   - walk out through the 🚪
 * What the player sees comes from state: hidden, on, open, locked, blocked_by (camera cone),
 * ringing, armed, value (dial), entries (record), signatures, walking, mood.
 * Inspecting is part of the OBJECTIVE, not a gate: any action works the moment the player spots the
 * issue, but the mission is only complete (and a right "leave" through the 🚪 only allowed) once every
 * visible item has been inspected (observe_first).
 *
 * Opening, same beats as the app's rooms (Ares Crane Bay's MissionBriefing -> IntroSequence -> Mission Notes):
 *   brief - a card with the mission and its goal (and, where offered, one visit or a full shift), "Begin"
 *   scene - the scene sets itself: items appear one by one while the background lines play as
 *           subtitles; nothing is clickable yet. Tapping the last line hands over control.
 *   play  - the goal stays in the 📋 Mission Notes card; the rooms' GameHeader gives ❓ How to Play
 *           (built from this mission's interactions) and 💡 Hints - two per step (a nudge, then how),
 *           always about the NEXT step on the shortest winning route from where the player is now.
 *
 * Moving things - no dragging, always "select, then click the target"; the target reacts
 * (pulses when it takes it, shakes when it doesn't):
 *   objects that go somewhere (key -> key cabinet, or out the 🚪) - click to collect into the 🎒
 *     (the rooms' shared InventoryBox), pick it from the 🎒, click the target
 *   people - click them, 🚶 in their ring, click where they go (an item or the 🚪)
 *   heavy things in the way (drop_on 'away') - click them, 🫸 pushes them clear
 *
 * The world lives (both modes): people stroll and WALK where they're led (out the door too), cameras
 * carry a status light, open things blink amber, the monitor runs a live timestamp, and a ☎️ on the
 * desk rings - in a visit, after a long idle spell, with a nudge about the next step.
 *
 * Two modes (`allowShift` offers the choice on the briefing, when shiftEngine says the mission can repeat):
 *   visit - put this scene right once (above)
 *   shift - the ATC loop: a shift clock runs, problems keep HAPPENING (someone walks over and leaves the
 *           pallet in front of the camera again, the customer comes back in), each with a deadline; a
 *           missed deadline rings the ☎️; slips are counted, not game over; closing steps (arm the alarm)
 *           wait for the end of the shift; the 🚪 hands the shift over. The debrief is the tally.
 */

// Generated images are served by the backend (see backend/main.py), not from the frontend's public/.
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);

const EMPTY_TALLY = { onTime: 0, late: 0, lost: 0, slips: 0, missed: 0, byGroup: {} };
const DOOR_LEFT = 93;                                   // where people come in and go out (the 🚪's side)
/** Someone walking: one picture cut at the hips - the body bobs, and the two legs swing in turn (a stride). */
function Walker({ src, alt }) {
  return (
    <span className="mg-walker" role="img" aria-label={alt}>
      <img className="mg-walk-body" src={src} alt="" draggable={false} />
      <img className="mg-walk-leg is-near" src={src} alt="" draggable={false} />
      <img className="mg-walk-leg is-far" src={src} alt="" draggable={false} />
    </span>
  );
}

const num = (v, fallback) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : fallback);
const SMALL_SCREEN = '(max-width: 900px), (max-height: 520px)';   // a phone, either way up

const CLOSING_GRACE = 30;                              // seconds to do a closing step once the shift is over
const shiftTime = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

/**
 * In a storyline (StorylinePlayer.jsx): `chapter` ("2 / 3") shows in the header, and winning offers
 * `onWin` - "Continue: <winLabel>" - which plays the transition into the next mission.
 */
export default function MissionGame({ spec, onClose, onWin = null, winLabel = '', chapter = '', allowShift = false, onRun = null,
  tryout = false, page = false }) {
  // tryout: trying inventory items - straight into play: no briefing, no mission statement or notes, and what happens
  // shows as a short toast instead of subtitles. page: the game fills its own browser window (not a dialog on a page).
  const exitRef = useRef(null);
  // One play, for onRun (a guest's published game - backend publish.py): the actions taken and when (seconds in).
  const run = useRef({ actions: [], start: null, sent: false });
  const endTimer = useRef(null);
  // A "moving first-person" mission opens by walking in (the storyline's corridor walk); in a storyline
  // the links between missions do the walking.
  const walkIn = allowShift && spec.mission?.template === 'moving_fpv';
  const [phase, setPhase] = useState(tryout ? 'play' : walkIn ? 'walk' : 'brief');      // ['walk' ->] 'brief' -> 'scene' -> 'play'
  const [ctx, setCtx] = useState(() => initialContext(spec));
  const [ending, setEnding] = useState(false);      // goal/fail reached, consequence still playing
  const [ended, setEnded] = useState(null);          // { outcome: 'goal'|'fail', lines }
  const [slips, setSlips] = useState([]);           // trap action ids performed
  const [open, setOpen] = useState(null);           // item id in close-up
  const [focus, setFocus] = useState(null);         // item showing its action ring (☝️ 🤝 🚶 🫸)
  const [awayIds, setAwayIds] = useState([]);       // moved out of the way / out the exit (visual, when no `hidden` effect)
  const [standsAt, setStandsAt] = useState({});     // item id -> item id it was taken to
  const [pulse, setPulse] = useState(null);         // { id, ok, key } - the target's reaction
  const [draftChips, setDraftChips] = useState([]);
  const [held, setHeld] = useState([]);             // carryable item ids picked up (in the 🎒)
  const [used, setUsed] = useState({});             // carryable item id -> true once used on its target
  const [using, setUsing] = useState(null);         // item being moved: picked from the 🎒, or a person being led
  const [reaction, setReaction] = useState(null);
  const [subtitle, setSubtitle] = useState({ key: 0, lines: [] });
  // on a phone the notes would cover the whole scene: they start folded there (📋 opens them)
  const [notesOpen, setNotesOpen] = useState(() => !(window.matchMedia?.(SMALL_SCREEN)?.matches));
  const [showAreas, setShowAreas] = useState(false);   // the areas the creator left unmarked (hidden): shown when asked for
  const [howToOpen, setHowToOpen] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [hintRevealed, setHintRevealed] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const hintsRef = useRef(0);
  hintsRef.current = hintsUsed;
  // ---- the living world + shift mode
  const plan = useMemo(() => shiftPlan(spec), [spec]);
  // A procedure ("do the job") plays once for now - in a shift, each new patient would bring a new prescription.
  const canShift = allowShift && plan.available && spec.type !== 'procedure' && spec.type !== 'flow';
  const [mode, setMode] = useState('visit');        // 'visit' | 'shift'
  const shiftOn = mode === 'shift';
  const [clock, setClock] = useState(0);            // seconds of play (in a shift: game minutes)
  const [openProblems, setOpenProblems] = useState({});   // group id -> { due, late }
  const [tally, setTally] = useState(EMPTY_TALLY);
  const [calls, setCalls] = useState([]);           // ☎️ queue: [{ key, line }]
  const [walkers, setWalkers] = useState({});       // id -> { left, bottom, ms } - someone walking somewhere
  const [facing, setFacing] = useState({});         // id -> 'left' | 'right' while on the move
  const [stroll, setStroll] = useState({});         // id -> small offset (%) - people shifting about
  const [stranger, setStranger] = useState(null);   // a passer-by when no one in the scene can bring it
  const [arrived, setArrived] = useState({});       // id -> key: just put back into the scene
  const [exiting, setExiting] = useState([]);       // people walking to the door before they're gone
  const timers = useRef([]);
  const nextSpawn = useRef(0);
  const arriving = useRef(false);
  const cooldown = useRef({});
  const lastAct = useRef(0);
  const calledFor = useRef(null);
  const lastCall = useRef(false);
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => () => { clearTimeout(endTimer.current); timers.current.forEach(clearTimeout); }, []);

  const byId = Object.fromEntries(spec.items.map((it) => [it.id, it]));
  // more sections of the work area (backend game.build_game `views`): the player walks between them by the signs in the
  // scene. A game with one view has none - as before.
  const sections = spec.views && spec.views.length > 1 ? spec.views : null;
  const [view, setView] = useState(sections ? sections[0].id : 'main');
  const [walking, setWalking] = useState(false);
  const st = (id) => ctx.state[id] || {};
  const observable = spec.items.filter((it) => !(it.state || {}).hidden && !it.scenery);
  const rights = spec.actions.filter((a) => a.kind === 'right');
  const allInspected = (c) => !spec.observe_first || observable.every((it) => c.seen.includes(it.id) || c.state[it.id]?.hidden === true);
  // Both halves of a problem pair are "about" the same problem (the box stack IS the blocked camera).
  // A record entry "about" the record itself means "about whatever you saw" - any subject will do.
  const aboutOf = (a) => (a.about && a.about !== a.item ? a.about : null);
  const partnersOf = (id) => [id, ...(spec.pairs || []).flatMap((p) => (p.host === id ? [p.part] : p.part === id ? [p.host] : []))];
  const pending = (a) => !ctx.done.includes(a.id);
  // One entry per control: several actions on the same item+interaction are a queue, shown once.
  const actsFor = (id, interaction) => spec.actions
    .filter((a) => a.item === id && a.interaction === interaction && pending(a))
    .filter((a, i, all) => all.findIndex((b) => slotKey(b) === slotKey(a)) === i);
  const exitActs = spec.actions.filter((a) => a.interaction === 'exit' && pending(a));
  const hasExit = spec.actions.some((a) => a.interaction === 'exit' || a.drop_on === 'exit');
  const started = phase !== 'brief' && phase !== 'walk';
  const playing = phase === 'play' && !ending;
  // 'drag' actions in the data are moves: to a target (an item or the exit), or 'away'.
  const moveActs = (id) => spec.actions.filter((a) => a.item === id && a.interaction === 'drag' && a.drop_on && a.drop_on !== 'away');
  const pushActs = (id) => actsFor(id, 'drag').filter((a) => !a.drop_on || a.drop_on === 'away');
  const carryable = spec.items.filter((it) => it.kind !== 'person' && moveActs(it.id).length > 0);
  const isCarryable = (id) => carryable.some((it) => it.id === id);
  const leadActs = (id) => (byId[id]?.kind === 'person' ? moveActs(id).filter(pending) : []);
  const react = (id, ok) => setPulse({ id, ok, key: Date.now() });

  // ---- hints: about the next step on the shortest winning route from here
  const nameOf = (id) => (id === 'exit' ? 'the 🚪' : byId[id]?.kind === 'person' ? byId[id].name : `the ${byId[id]?.name || id}`);
  // Hint words come from the action's interaction plugin (./mechanics/interactions.js).
  const helpers = { nameOf, aboutOf, isCarryable, byId };
  const howTo = (a) => howToFor(a, helpers);
  const nudge = (a) => nudgeFor(a, helpers);
  const shiftOver = shiftOn && clock >= SHIFT_SECONDS;
  const openKey = Object.keys(openProblems).sort().join(',');
  const route = useMemo(() => {
    if (phase !== 'play') return null;
    if (!shiftOn) return nextSteps(spec, ctx);
    // In a shift: the next step on the problems open right now (closing steps once the shift is over).
    const openNow = plan.groups.filter((g) => openKey.split(',').includes(g.id));
    const closing = plan.closingActions.filter((id) => !ctx.done.includes(id));
    const goal = openNow.length ? { all: openNow.flatMap((g) => g.goal) } : shiftOver && closing.length ? { all: closing.map((id) => ({ done: id })) } : null;
    if (!goal) return null;
    const actions = spec.actions.filter((a) => a.kind === 'right' && (shiftOver || !plan.closingActions.includes(a.id)));
    return nextSteps({ ...spec, fail: null, goal, actions }, ctx);
  }, [spec, ctx, phase, shiftOn, openKey, shiftOver, plan]);
  const unseen = observable.filter((it) => !ctx.seen.includes(it.id) && !st(it.id).hidden);
  const hint = (() => {
    const next = route && route.length ? spec.actions.find((a) => a.id === route[0]) : null;
    if (shiftOn && !next) return { key: 'quiet', lines: ['All quiet for now - keep an eye on the floor.'] };
    if (!shiftOn && (!next || next.interaction === 'exit') && spec.observe_first && unseen.length) {
      return { key: 'look', lines: ["You haven't checked everywhere yet.", `Click ${unseen.map((it) => nameOf(it.id)).join(', ')}.`] };
    }
    if (!next) return { key: 'none', lines: ["There's nothing left to put right from here."] };
    return { key: next.id, lines: [nudge(next), howTo(next)] };
  })();
  useEffect(() => { setHintRevealed(0); }, [hint.key]);
  const showHint = () => {
    setHintOpen(true);
    if (hintRevealed === 0) { setHintRevealed(1); setHintsUsed((n) => n + 1); }
  };
  const revealNextHint = () => { setHintRevealed((n) => n + 1); setHintsUsed((n) => n + 1); };

  const howToPlayText = [
    `Your goal: ${spec.objective.join(' ')}`,
    '',
    '👁 Inspect: click items to check them (the counter at the top). Problems show in the scene - a red camera view, something propped or left where it shouldn\'t be. Act on a problem as soon as you spot it; the mission is complete once everything is checked and put right.',
    '✋ Act on the items themselves - there are no buttons:',
    ...[...new Set(spec.actions.map((a) => controlOf(a, helpers)))].map((k) => `   ${CONTROLS.get(k)?.line}`).filter((l) => l.trim()),
    '⏱ Order matters. Doing things in the wrong order can lose the mission.',
    ...(shiftOn ? [
      `⟳ A full shift (${shiftTime(SHIFT_SECONDS)}): things keep going wrong while you're on duty - people leave things where they shouldn't, customers come back. Put each right as soon as it shows; a slip is counted, it doesn't end the shift.`,
      '☎️ When something has been left too long, the phone rings - pick it up.',
      '🚪 The door hands the shift over (anything still wrong counts against you).'
    ] : ['☎️ If the phone rings, pick it up.']),
    '💡 Stuck? Hints are about your next step.'
  ].join('\n');

  const say = (lines) => setSubtitle((s) => ({ key: s.key + 1, lines: lines.filter(Boolean).map((text) => ({ text, duration: 3400 })) }));
  const show = (feedbacks) => {
    say(feedbacks.flatMap((f) => f.say || []));
    const r = [...feedbacks].reverse().find((f) => f.reaction && REACTIONS.get(f.reaction.kind));
    if (r) setReaction({ who: r.reaction.who, emoji: REACTIONS.get(r.reaction.kind).emoji, key: Date.now() });
  };
  const look = (id) => setCtx((c) => (c.seen.includes(id) ? c : { ...c, seen: [...c.seen, id] }));

  /** Performs the action through the engine. Returns true when it took effect. */
  const attempt = (picked) => {
    if (!playing || !picked || !pending(picked)) return false;
    const action = resolveSlot(spec, picked, ctx);     // one control, several actions -> the next one in its queue
    if (shiftOn && !shiftOver && plan.closingActions.includes(action.id)) {
      say(["That's for the end of the shift."]);
      return false;
    }
    const out = perform(spec, action, ctx);
    show(out.feedback);
    if (out.result === 'blocked') return false;
    run.current.actions.push({ id: action.id, t: Math.round((Date.now() - (run.current.start || Date.now())) / 1000) });
    lastAct.current = clock;
    if (action.kind === 'trap') setSlips((s) => [...s, action.id]);
    if (shiftOn) {
      // A slip is counted and the shift goes on; only the closing step going off ends it early.
      if (action.kind === 'trap') {
        setTally((t) => ({ ...t, slips: t.slips + 1 }));
        setCtx(forgiveSlip(out.ctx, action.id));
      } else setCtx(out.ctx);
      if (action.kind !== 'trap' && spec.fail && evaluate(spec.fail, out.ctx)) endShift(out.feedback.flatMap((f) => f.say || []));
      return true;
    }
    setCtx(out.ctx);
    const outcome = status(spec, out.ctx);
    const lines = out.feedback.flatMap((f) => f.say || []);
    if (outcome === 'goal' && !allInspected(out.ctx)) {
      say([...lines, "Everything's put right - but you haven't checked everywhere yet."]);
    } else if (outcome === 'goal' && spec.summary?.length) {
      // Finishing isn't winning: it's a win (the success line, Continue) only with every summary line right.
      const allRight = spec.summary.every((c) => evaluate(c.when, out.ctx));
      finish(allRight ? 'goal' : 'fail', allRight && spec.success ? [...lines, spec.success] : lines);
    } else if (outcome !== 'playing') {
      if (outcome === 'dead_end') lines.push(...((spec.dead_end || {}).say || ["It's too late to put that right now."]));
      finish(outcome, lines);
    }
    return true;
  };

  const reportRun = (outcome) => {
    const r = run.current;
    if (!onRun || r.sent || (outcome === 'left' && !r.start)) return;
    r.sent = true;
    onRun({ outcome, actions: r.actions, hints: hintsRef.current, seconds: r.start ? Math.round((Date.now() - r.start) / 1000) : 0 });
  };
  const close = () => { if (!ended) reportRun('left'); onClose(); };
  useEffect(() => { if (ended) reportRun(ended.outcome); }, [ended]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Let the consequence play out in the scene first, then close on it.
  const finish = (outcome, lines) => {
    setEnding(true);
    setOpen(null);
    endTimer.current = setTimeout(() => setEnded({ outcome, lines }), 2600);
  };
  /** The shift is handed over: anything still wrong is counted as left open. */
  const endShift = (lines) => {
    const left = Object.keys(openProblems);
    setTally((t) => {
      const byGroup = { ...t.byGroup };
      left.forEach((gid) => { byGroup[gid] = { ...(byGroup[gid] || {}), missed: (byGroup[gid]?.missed || 0) + 1 }; });
      return { ...t, missed: t.missed + left.length, byGroup };
    });
    setOpenProblems({});
    finish('shift', lines);
  };
  // The goal was already met and the last unchecked item has just been looked at -> complete.
  useEffect(() => {
    if (!shiftOn && phase === 'play' && !ending && spec.observe_first && allInspected(ctx) && status(spec, ctx) === 'goal') {
      finish('goal', ['Everything checked, everything put right.']);
    }
  }, [ctx.seen.length]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The selected thing (from the 🎒, or a person being led) is applied to a target id (an item or 'exit'). */
  const applyHeldTo = (targetId) => {
    // The right target takes it. Anywhere else, a thing with an 'away' move (usually a trap: "leave the
    // keys out") is simply set down there; otherwise the target refuses it.
    const action = moveActs(using).find((a) => a.drop_on === targetId && pending(a))
      || (isCarryable(using) ? pushActs(using)[0] : null);
    if (!action) { react(targetId, false); say(["That doesn't go there."]); return; }
    if (!attempt(action)) { react(targetId, false); return; }
    react(targetId, true);
    if (shiftOn && action.kind === 'trap') {        // a slip in a shift: counted, and the thing is back where it was
      setHeld((h) => h.filter((id) => id !== using));
      setUsing(null);
      return;
    }
    if (isCarryable(using)) setUsed((u) => ({ ...u, [using]: true }));
    if (targetId === 'exit' && byId[using]?.kind === 'person') {
      const who = using;                      // they walk to the door, then they're gone
      setExiting((x) => [...x, who]);
      walk(who, { left: DOOR_LEFT, bottom: posOf(who).bottom }, () => {
        setAwayIds((m) => [...m, who]);
        setExiting((x) => x.filter((id) => id !== who));
        settle(who);
      });
    } else if (targetId === 'exit') setAwayIds((m) => [...m, using]);
    else {
      if (byId[using]?.kind === 'person') stepFor(using, num(byId[targetId]?.style?.left, 50) < posOf(using).left ? 'left' : 'right');
      setStandsAt((st2) => ({ ...st2, [using]: targetId }));
    }
    setUsing(null);
  };

  // ---- people moving: where someone is now, walking them somewhere, and letting them stand again
  const posOf = (id) => {
    if (walkers[id]) return walkers[id];
    const s = byId[id]?.style || {};
    const at = standsAt[id] && byId[standsAt[id]]?.style;
    return { left: num(at ? at.left : s.left, 50) + (at ? 3 : stroll[id] || 0), bottom: num(at ? at.bottom : s.bottom, 8), width: num(s.width, 8) };
  };
  const stepFor = (id, dir, ms = 1800) => {         // bob + face the way they're going while the CSS walk plays
    setFacing((f) => ({ ...f, [id]: dir }));
    later(() => setFacing((f) => { const n = { ...f }; delete n[id]; return n; }), ms);
  };
  const walk = (id, to, then) => {
    const from = posOf(id);
    const ms = Math.max(800, Math.hypot(to.left - from.left, to.bottom - from.bottom) * 60);
    stepFor(id, to.left < from.left ? 'left' : 'right', ms);
    setWalkers((w) => ({ ...w, [id]: { left: to.left, bottom: to.bottom, ms } }));
    if (then) later(then, ms + 50);
  };
  const settle = (id) => setWalkers((w) => { const n = { ...w }; delete n[id]; return n; });

  const inPlace = (id) => ['hold', 'switch', 'adjust'].some((k) => actsFor(id, k).length > 0) || typeof st(id).amount === 'number'
    || !!byId[id]?.contents;
  const take = (id) => { setHeld((h) => [...h, id]); react(id, true); setOpen(null); };
  // ---- the generic core's bag, room-style (the rooms' 🎒 tray): a loose thing with nothing else to show or do is picked
  // up with a click; one in the tray is picked there, then used by clicking where it goes (a shelf, a bin, a person, what
  // it's used on). The close-up's buttons (🎒 Take it, Put it here) still do the same.
  const inBag = (id) => !!id && st(id).where === 'bag';
  const firstWhere = useRef(null);                     // where each thing starts - so the tray knows one put away
  if (!firstWhere.current) firstWhere.current = Object.fromEntries(spec.items.map((it) => [it.id, st(it.id).where]));
  // an object drawn per state (a frayed seat belt, then the new one): the picture of how it is now, else its one picture
  const pictureOf = (it) => it.state_src?.[st(it.id).stage] || it.src;
  const pickUp = (it) => {
    // a loose thing - or one seen inside an open holder (its "take" is the holder's): a click puts it in the 🎒
    const own = spec.actions.find((a) => a.move?.kind === 'take' && a.move.thing === it.id && (a.item === it.id || holderOf(it))
      && pending(a) && evaluate(a.requires, ctx));
    const more = spec.actions.some((a) => a.item === it.id && !a.move && a.kind !== 'undo')
      || !!it.inv?.states?.[st(it.id).stage]?.conveys || !!it.inv?.container;
    if (!own || more) return false;
    if (attempt(own)) react(it.id, true);
    return true;
  };
  const putFromBag = (targetId) => {
    const all = spec.actions.filter((a) => a.move && a.move.thing === using && a.item === targetId && ['put', 'combine'].includes(a.move.kind));
    // the same tool for two phases (the cloth wipes it, later dries it): the one that can be done now
    const ready = all.filter((a) => a.move.kind === 'combine' && evaluate(a.requires, ctx));
    const acts = all.some((a) => a.move.kind === 'combine') && all.length > 1 ? (ready.length ? ready : all.slice(0, 1)) : all;
    setUsing(null);
    if (acts.length > 1) { setOpen(targetId); return; }          // which shelf, which bin: chosen in its close-up
    if (acts.length && attempt(acts[0])) { react(targetId, true); return; }
    react(targetId, false);
    if (!acts.length) say([byId[using]?.inv?.use === 'tool' ? 'That does nothing here.' : "That doesn't go there."]);
  };
  const notesFolded = useRef(false);
  const clickItem = (it) => {
    if (!playing) return;
    // The notes card covers part of the scene: it folds away on the first click into the scene
    // (the objective stays in the header; 📋 reopens it).
    if (!notesFolded.current) { notesFolded.current = true; setNotesOpen(false); }
    if (it.scenery) return;                      // scenery isn't part of the job
    look(it.id);
    if (inBag(using)) {                          // something from the 🎒 tray, used on what was clicked
      if (it.id === using) setUsing(null);
      else putFromBag(it.id);
      return;
    }
    if (it.inv && !using) {                      // an inventory item opens to what it offers - or, a loose thing, goes in the 🎒
      if (pickUp(it)) return;
      setDraftChips([]); setOpen(it.id); return;
    }
    if (using) {
      if (it.id === using) setUsing(null);       // clicking the person being led again lets go
      else applyHeldTo(it.id);
      return;
    }
    // Something you carry goes straight into the 🎒 - unless it also has something to do in place (measure
    // it, press it): then it opens first, with 🎒 Take it in the close-up.
    if (isCarryable(it.id) && !held.includes(it.id) && !inPlace(it.id)) {
      take(it.id);
      return;
    }
    if (it.kind === 'person') {
      say(it.remote ? (it.says || []).map((line) => `☎️ ${it.name}: “${line}”`) : (it.says || []));
      if (actsFor(it.id, 'gesture').length || actsFor(it.id, 'hold').length || leadActs(it.id).length) setFocus(it.id);
      return;
    }
    if (pushActs(it.id).length) { setFocus(it.id); return; }
    setDraftChips([]);
    setOpen(it.id);
  };

  const leave = () => {
    if (!playing) return;
    if (using) { applyHeldTo('exit'); return; }
    if (shiftOn) {
      const walkAway = spec.actions.find((a) => a.interaction === 'exit' && a.kind === 'trap');
      endShift(Object.keys(openProblems).length
        ? (walkAway?.feedback?.say || ['You hand over with things still wrong.'])
        : ['You hand the shift over.']);
      return;
    }
    if (exitActs.length && exitActs[0].kind === 'right' && !allInspected(ctx)) {
      say(["You haven't checked everywhere yet."]);
      return;
    }
    if (exitActs.length) attempt(exitActs[0]);
    else say(['There is still work to do here.']);
  };

  const restart = (nextMode = mode) => {
    if (!ended) reportRun('left');
    run.current = { actions: [], start: null, sent: false };
    clearTimeout(endTimer.current);
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setCtx(initialContext(spec));
    setEnding(false); setEnded(null); setSlips([]); setOpen(null); setFocus(null); setAwayIds([]); setStandsAt({}); setDraftChips([]); setPulse(null);
    setHeld([]); setUsed({}); setUsing(null); setHintOpen(false); setHintsUsed(0);
    setReaction(null);
    setMode(nextMode); setClock(0); setOpenProblems({}); setTally(EMPTY_TALLY); setCalls([]); setWalkers({}); setFacing({});
    setStroll({}); setStranger(null); setArrived({}); setExiting([]);
    nextSpawn.current = 0; arriving.current = false; cooldown.current = {}; lastAct.current = 0; calledFor.current = null; lastCall.current = false;
    setPhase('scene');
  };

  /** Shift starts: the problems already in the scene are the first ones on the clock. */
  const beginPlay = () => {
    run.current.start = run.current.start || Date.now();
    setPhase('play');
    if (!shiftOn) return;
    const scale = pace(0, plan.groups.length).deadlineScale;
    setOpenProblems(Object.fromEntries(plan.groups.filter((g) => !groupSolved(g, ctx))
      .map((g) => [g.id, { due: deadlineSeconds(g, scale) + 10, late: false }])));
    nextSpawn.current = 20;
  };

  // A thing ON / UNDER / OVER something (the cart on the bay, boxes on the shelf) is drawn there - and slides there when
  // it's moved. A thing IN something (towels in a cupboard) is drawn inside it, in rows - but only while it's open (its
  // `open_in` states; one with no door is always open): shut, what's inside is hidden. A holder that's out of sight
  // itself (a linen bag in your 🎒) shows nothing.
  const SHOWN_ON = ['on', 'under', 'over'];
  const holderOf = (it, depth = 0) => {
    const where = st(it.id).where;
    if (!where || ['scene', 'bag', 'nowhere'].includes(where) || depth > 3) return null;
    const holder = byId[where.split(':')[0]];
    if (!holder || ![...SHOWN_ON, 'in'].includes(holder.inv?.holds)) return null;
    if (holder.inv.holds === 'in' && holder.inv.open_in && !holder.inv.open_in.includes(st(holder.id).stage)) return null;
    const out = holder.inv?.collectible && st(holder.id).where !== undefined && st(holder.id).where !== 'scene'
      && !holderOf(holder, depth + 1);
    return out ? null : holder;
  };
  // Something standing in front of another (a status list's `behind`): until it's moved from where it started, it
  // stands in front of the thing it blocks.
  const blockedBy = (it) => {
    const front = it.inv?.blocked_by && byId[it.inv.blocked_by];
    return front && st(front.id).where === (front.state || {}).where ? front : null;
  };
  const blocking = (it) => spec.items.find((b) => b.inv?.blocked_by === it.id && blockedBy(b));
  const edgeOf = (style, lift) => (style.bottom !== undefined ? { bottom: `calc(${style.bottom} + ${lift}%)` }
    : style.top !== undefined ? { top: `calc(${style.top} - ${lift}%)` } : { bottom: `${10 + lift}%` });
  // what's IN a holder, or carried and put ON one (cups on a cart, a kettle on the counter), is drawn inside the holder's
  // own box (renderItem) - in a grid a tenth in from its edges, or in a row standing on its top - so it fits whatever its
  // picture's real size. Something pushed onto a spot (the cart onto its bay) stands there in the room (placedStyle).
  const nestedIn = (it) => {
    const h = detached(it) ? null : holderOf(it);
    return h && (h.inv.holds === 'in' || (h.inv.holds === 'on' && it.inv?.carried !== false)) ? h : null;
  };
  // how big a thing is drawn on / in a holder, as a share of the holder's width: its own size beside it - never more
  // than 30% (a cup in a sink stays cup-sized), shrunk only when many share the space
  const shareOf = (thing, holder) => Math.min((100 * num((thing.style || {}).width, 6)) / num((holder.style || {}).width, 14), 30);
  const placedStyle = (it, depth = 0) => {
    const holder = detached(it) ? null : holderOf(it);
    if (holder) {
      // things sharing a holder (six bottles on one counter) stand side by side along it, centred on it - never in a heap:
      // each keeps its own size, shrunk only when there are too many to fit in a row a little wider than the holder
      const hs = (depth < 3 && placedStyle(holder, depth + 1)) || holder.style || {};
      const mates = spec.items.filter((x) => holderOf(x)?.id === holder.id && !nestedIn(x));   // (not those on its top)
      const k = mates.findIndex((x) => x.id === it.id);
      const lift = { on: 3, under: -3, over: 18 }[holder.inv.holds];
      const hw = num(hs.width, 12);
      const room = Math.max(hw * 1.6, 24);                  // the row may run a little past the holder's ends
      const w = Math.min(num((it.style || {}).width, 8), room / mates.length - 1);
      const step = w + 1;
      const left = Math.min(Math.max(num(hs.left, 40) + hw / 2 - (mates.length * step) / 2 + k * step, 0), 100 - w);
      return { ...(it.style || {}), top: undefined, bottom: undefined, ...edgeOf(hs, lift), left: `${left}%`,
        width: `${w}%`, zIndex: (hs.zIndex || 5) + (holder.inv.holds === 'under' ? -1 : 1) };
    }
    const back = blocking(it);
    if (back && back.style) {
      const bs = back.style;
      return { ...(it.style || {}), top: undefined, bottom: undefined, ...edgeOf(bs, 0), left: `calc(${bs.left} - 2%)`, zIndex: (bs.zIndex || 5) + 2 };
    }
    return null;
  };

  // something moved (not carried) INTO a holder - the forklift into the trailer - isn't drawn there: the holder's board
  // says what's inside it
  const boardOf = (it) => {
    const h = it.inv?.carried === false && !detached(it) ? holderOf(it) : null;
    return h && h.inv?.holds === 'in' && !h.inv?.zone ? h : null;
  };
  // which view a thing is in: the one the creator put it in, while it's still where it started - else, on or in
  // something (the pallet pushed onto the dock's bay), its holder's
  const stillAtStart = (it) => st(it.id).where === firstWhere.current?.[it.id];
  const viewOf = (it, depth = 0) => {
    if (it.view && stillAtStart(it)) return it.view;
    const h = depth < 4 ? holderOf(it) : null;
    return h ? viewOf(h, depth + 1) : (it.view || 'main');
  };
  // a thing still where it starts is drawn where the creator placed it in the arranger (backend game.apply_layout:
  // placed_by "creator"), not lined up on its holder: in another view than what it stands on, or on / under / over
  // something (inside something it's still drawn in it - shut, it's hidden). Once the player moves it, it's drawn on what
  // it went on. Never placed by the creator: lined up on its holder, at its own size (the layout's width).
  function detached(it) {
    const h = stillAtStart(it) ? holderOf(it) : null;
    if (!h) return false;
    if (sections && it.view) return viewOf(h) !== it.view;
    return SHOWN_ON.includes(h.inv.holds) && it.placed_by === 'creator';
  }
  const inView = (it) => !sections || viewOf(it) === view;
  const goTo = (id) => {
    setWalking(true);
    setTimeout(() => { setView(id); setWalking(false); }, 350);
  };
  const viewHere = sections && sections.find((v) => v.id === view);
  // a zone (an area of the floor: a parking bay, the walkway, the space kept clear in front of an exit) is marked on the
  // floor, never painted - its kind says how (backend zone.yaml); one the creator moved keeps a floor-shaped height
  const zoneOf = (it) => (it.kind === 'place' && it.inv?.zone) || null;
  // how an area is marked - the creator's pick in the arranger (backend game.AREA_LOOKS); none picked: not marked
  const areaLook = (it) => it.area_look || 'hidden';
  const styleFor = (it) => {
    if (zoneOf(it)) return { ...(it.style || {}), height: (it.style || {}).height || `${(num((it.style || {}).width, 20) * 0.6).toFixed(1)}%` };
    const base = placedStyle(it) || it.style || {};
    const w = walkers[it.id];
    if (w) return { ...base, left: `${w.left}%`, bottom: `${w.bottom}%`, zIndex: 12, transition: `left ${w.ms}ms linear, bottom ${w.ms}ms linear, opacity 0.6s` };
    const target = standsAt[it.id] && byId[standsAt[it.id]]?.style;
    if (target) return { ...base, left: `calc(${target.left} + 3%)`, bottom: target.bottom, zIndex: 12 };
    return stroll[it.id] ? { ...base, left: `calc(${base.left} + ${stroll[it.id]}%)` } : base;
  };

  // ---- the clock: one tick a second while playing
  useEffect(() => {
    if (phase !== 'play' || ending) return undefined;
    const t = setInterval(() => setClock((c) => c + 1), 1000);
    return () => clearInterval(t);
  }, [phase, ending]);

  const free = (id) => !walkers[id] && !standsAt[id] && using !== id && focus !== id && !exiting.includes(id)
    && !awayIds.includes(id) && st(id).hidden !== true;
  // Someone on the phone is a telephone, not a person in the room: never strolls, never walks anything over.
  const people = spec.items.filter((it) => it.kind === 'person' && !it.remote);
  const inProblems = new Set(plan.groups.flatMap((g) => g.items));

  /** Someone in the scene shifts about (a walker strolls further). */
  const strollSomeone = () => {
    const idle = people.filter((it) => free(it.id));
    if (!idle.length) return;
    const it = idle[Math.floor(Math.random() * idle.length)];
    const range = st(it.id).walking ? 9 : 3;
    const next = stroll[it.id] ? 0 : (Math.random() < 0.5 ? -1 : 1) * range * (0.5 + Math.random() / 2);
    stepFor(it.id, next < (stroll[it.id] || 0) ? 'left' : 'right');
    setStroll((s) => ({ ...s, [it.id]: next }));
  };

  const ring = (line) => setCalls((c) => [...c, { key: `${Date.now()}-${c.length}`, line }]);
  const answer = () => {
    if (!calls.length) { say(['Dial tone.']); return; }
    const [call, ...rest] = calls;
    setCalls(rest);
    say([`☎️ “${call.line}”`]);
  };

  /** A problem happens (again): its own person walks back in, or someone walks over and leaves it there. */
  const happen = (g, scale) => {
    arriving.current = true;
    const start = () => {
      setCtx((c) => rearm(spec, g, c));
      setAwayIds((a) => a.filter((id) => !g.items.includes(id)));
      setStandsAt((s) => Object.fromEntries(Object.entries(s).filter(([id]) => !g.items.includes(id))));
      setHeld((h) => h.filter((id) => !g.items.includes(id)));
      setUsed((u) => Object.fromEntries(Object.entries(u).filter(([id]) => !g.items.includes(id))));
      setUsing((u) => (g.items.includes(u) ? null : u));
      setArrived(Object.fromEntries(g.items.map((id) => [id, Date.now()])));
      setOpenProblems((op) => ({ ...op, [g.id]: { due: clock + 3 + deadlineSeconds(g, scale), late: false } }));
    };
    if (g.person) {                               // the customer / visitor comes back in through the door
      const home = posOf(g.person);
      start();
      setWalkers((w) => ({ ...w, [g.person]: { left: DOOR_LEFT, bottom: home.bottom, ms: 0 } }));
      later(() => walk(g.person, { left: num(byId[g.person].style?.left, 50), bottom: num(byId[g.person].style?.bottom, 8) },
        () => { settle(g.person); arriving.current = false; }), 80);
      return;
    }
    // The thing that comes back (the pallet, the keys) - the courier walks along the floor to below it.
    const back = g.items.find((id) => (byId[id].state || {}).hidden === false && (st(id).hidden === true || awayIds.includes(id))) || g.anchor;
    const target = byId[back]?.style || {};
    const courier = people.find((it) => !inProblems.has(it.id) && free(it.id));
    const floor = courier ? posOf(courier.id).bottom : 6;
    const spot = { left: Math.min(88, Math.max(2, num(target.left, 50) + num(target.width, 8) / 2 - 3)), bottom: floor };
    if (courier) {
      const home = posOf(courier.id);
      walk(courier.id, spot, () => {
        start();
        if (courier.says?.length) say([`${courier.name}: “${courier.says[0]}”`]);
        later(() => walk(courier.id, home, () => { settle(courier.id); arriving.current = false; }), 900);
      });
      return;
    }
    const ms = Math.max(900, Math.abs(DOOR_LEFT - spot.left) * 60);
    setStranger({ left: DOOR_LEFT, bottom: floor, ms: 0, dir: 'left' });
    later(() => setStranger({ ...spot, ms, dir: 'left' }), 80);
    later(() => {
      start();
      later(() => setStranger({ left: DOOR_LEFT, bottom: floor, ms, dir: 'right' }), 900);
      later(() => { setStranger(null); arriving.current = false; }, 900 + ms + 100);
    }, ms + 120);
  };

  // Every tick: people move, the phone may ring, and in a shift: deadlines, new problems, the end.
  useEffect(() => {
    if (phase !== 'play' || ending) return;
    if (clock % 4 === 0) strollSomeone();
    if (!shiftOn) {
      // A long quiet spell: the phone rings with a nudge about the next step (once per step).
      if (clock - lastAct.current >= 45 && hint.key !== 'none' && calledFor.current !== hint.key && !calls.length) {
        calledFor.current = hint.key;
        ring(hint.lines[0]);
      }
      return;
    }
    const overdue = Object.entries(openProblems).filter(([, p]) => !p.late && clock >= p.due).map(([gid]) => gid);
    if (overdue.length) {
      setOpenProblems((op) => Object.fromEntries(Object.entries(op).map(([gid, p]) => [gid, overdue.includes(gid) ? { ...p, late: true } : p])));
      overdue.forEach((gid) => {
        const g = plan.groups.find((x) => x.id === gid);
        ring(`${byId[g.anchor]?.name || 'Something'} - that's been left far too long.`);
      });
    }
    if (shiftOver) {
      // The shift ends on its clock. Only a closing step (arm the alarm last) gets a short grace, and only
      // when nothing else is still wrong; anything still wrong counts as left open.
      const closing = plan.closingActions.filter((id) => !ctx.done.includes(id));
      const open = Object.keys(openProblems).length;
      if (closing.length && !open && clock < SHIFT_SECONDS + CLOSING_GRACE) {
        if (!lastCall.current) { lastCall.current = true; say(['The shift is over - close down.']); }
        return;
      }
      if (!arriving.current) endShift([open ? 'The shift is over - with things still wrong.' : 'The shift is over.']);
      return;
    }
    const p = pace(clock / SHIFT_SECONDS, plan.groups.length);
    if (clock < nextSpawn.current || arriving.current || Object.keys(openProblems).length >= p.maxOpen) return;
    const ready = plan.groups.filter((g) => !openProblems[g.id] && (cooldown.current[g.id] || 0) <= clock);
    if (!ready.length) return;
    happen(ready[Math.floor(Math.random() * ready.length)], p.deadlineScale);
    nextSpawn.current = clock + p.gapSeconds + Math.floor(Math.random() * 4);
  }, [clock]); // eslint-disable-line react-hooks/exhaustive-deps

  // In a shift: a problem closes the moment it's put right (or can't be any more).
  useEffect(() => {
    if (!shiftOn || phase !== 'play') return;
    const closed = Object.entries(openProblems).map(([gid, p]) => {
      const g = plan.groups.find((x) => x.id === gid);
      return groupSolved(g, ctx) ? [g, p.late ? 'late' : 'onTime'] : groupLost(spec, g, ctx) ? [g, 'lost'] : null;
    }).filter(Boolean);
    if (!closed.length) return;
    setOpenProblems((op) => Object.fromEntries(Object.entries(op).filter(([gid]) => !closed.some(([g]) => g.id === gid))));
    closed.forEach(([g]) => { cooldown.current[g.id] = clock + 8; });
    setTally((t) => {
      const next = { ...t, byGroup: { ...t.byGroup } };
      closed.forEach(([g, how]) => {
        next[how] += 1;
        next.byGroup[g.id] = { ...(next.byGroup[g.id] || {}), [how]: (next.byGroup[g.id]?.[how] || 0) + 1 };
      });
      return next;
    });
    const lost = closed.find(([, how]) => how === 'lost');
    if (lost) later(() => say((spec.dead_end || {}).say || ["It's too late to put that right properly now."]), 1500);
    else setReaction({ who: closed[0][0].anchor, emoji: closed[0][1] === 'late' ? '⏰' : '✅', key: Date.now() });
  }, [ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  const badges = badgesFor;

  const renderItem = (it, index, nested = null) => {
    const s = st(it.id);
    const contents = it.inv?.holds ? spec.items.filter((x) => nestedIn(x)?.id === it.id) : [];
    const cols = contents.length <= 3 ? contents.length : Math.ceil(Math.sqrt(contents.length));
    // nested: the width it's given in its holder's grid cell / row
    const style = nested ? { '--nested-w': `${nested}%`, width: `${nested}%` } : styleFor(it);
    const gone = !exiting.includes(it.id) && (s.hidden === true || awayIds.includes(it.id) || (held.includes(it.id) && !used[it.id])
      || (s.where !== undefined && s.where !== 'scene' && !holderOf(it)) || !!boardOf(it));   // in the bag, or put inside something
    const inside = it.inv?.holds === 'in' ? spec.items.filter((x) => boardOf(x)?.id === it.id) : [];
    const reacting = pulse && pulse.id === it.id;
    const classes = ['mg-item', `mg-kind-${it.kind}`, ...(zoneOf(it) ? [`mg-zone mg-zone-${zoneOf(it)} mg-zone-look-${areaLook(it)}`] : []), ctx.seen.includes(it.id) ? 'is-seen' : '', gone ? 'is-moved' : '',
      facing[it.id] ? `is-stepping is-facing-${facing[it.id]}` : '', arrived[it.id] && !gone ? 'is-arriving' : '',
      it.remote ? 'is-remote' : '', it.remote && !ctx.seen.includes(it.id) && phase === 'play' ? 'is-calling' : '',
      it.inv && it.inv.states[s.stage]?.effect === 'ring' && phase === 'play' ? 'is-calling' : '',
      it.inv && it.inv.states[s.stage]?.effect === 'glow' ? 'is-glowing' : '', it.scenery ? 'is-scenery' : '',
      it.inv && it.inv.states[s.stage]?.effect === 'gone' ? 'is-gone' : '',
      it.inv && it.inv.states[s.stage]?.shift ? 'is-shifted' : '',
      it.inv && it.inv.states[s.stage]?.anim && s.stage !== (it.inv.start ?? Object.keys(it.inv.states)[0]) ? `anim-${it.inv.states[s.stage].anim}` : '',
      !pictureOf(it) && it.kind !== 'place' ? 'is-unpictured' : '',
      s.ringing ? 'is-ringing' : '', using && using !== it.id ? 'is-target' : '', using === it.id ? 'is-selected' : '',
      ...[it.inv?.collectible && 'is-movable', blockedBy(it) && 'is-blocked', holderOf(it) && `is-${holderOf(it).inv.holds}-holder`,
        it.flipped && 'is-flipped'].filter(Boolean),
      reacting ? (pulse.ok ? 'is-accepting' : 'is-refusing') : ''].join(' ');
    return (
      <div key={reacting ? `${it.id}-${pulse.key}` : arrived[it.id] ? `${it.id}-${arrived[it.id]}` : `${it.id}-${s.stage || ''}`} className={classes}
        style={{ ...style, '--i': index, '--w': parseFloat(style.width) || 8 }} title={it.name}
        {...(it.inv?.visual ? { 'data-visual': it.inv.visual, 'data-stage': s.stage } : {})}
        onClick={(e) => { e.stopPropagation(); clickItem(it); }}>
        {zoneOf(it) ? <span className="mg-zone-mark"><span className="mg-zone-name">{it.name}</span></span>
          : it.kind === 'place' ? <span className="mg-place-ring" />
          : pictureOf(it) ? (it.kind === 'person' && facing[it.id] ? <Walker src={asset(pictureOf(it))} alt={it.name} />
            : <img src={asset(pictureOf(it))} alt={it.name} draggable={false} />)
            : <span className="mg-fallback">{it.inv?.icon || (it.remote ? '☎️' : '❔')}</span>}
        {it.inv?.label && <span className="mg-inv-label">{it.inv.label}</span>}
        {inside.length > 0 && <span className="mg-holder-board">Inside: {inside.map((x) => x.name).join(', ')}</span>}
        {contents.length > 0 && it.inv.holds === 'in' && (
          <span className="mg-inside" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${Math.ceil(contents.length / cols)}, minmax(0, 1fr))` }}>
            {contents.map((x, k) => renderItem(x, k, Math.min(100, (shareOf(x, it) / 0.8) * cols)))}
          </span>
        )}
        {contents.length > 0 && it.inv.holds === 'on' && (
          <span className="mg-on-top">
            {contents.map((x, k) => renderItem(x, k, Math.min(shareOf(x, it) / 0.9, 100 / contents.length - 2)))}
          </span>
        )}
        <ItemExtras it={it} s={s} g={views} />
        {badges(s).length > 0 && <span className="mg-badges">{badges(s).map((b) => <span key={b}>{b}</span>)}</span>}
        {reaction && reaction.who === it.id && <span key={reaction.key} className="mg-bubble">{reaction.emoji}</span>}
        {focus === it.id && (
          <span className="mg-gestures" onClick={(e) => e.stopPropagation()}>
            {actsFor(it.id, 'gesture').map((a) => (
              <Captioned key={a.id} caption={a.name}>
                <button type="button" title={a.name} onClick={() => { setFocus(null); attempt(a); }}>☝️</button>
              </Captioned>
            ))}
            {it.kind === 'person' && actsFor(it.id, 'hold').map((a) => (
              <Captioned key={a.id} caption={a.name}><HoldButton label="🤝" onDone={() => { setFocus(null); attempt(a); }} /></Captioned>
            ))}
            {leadActs(it.id).length > 0 && (
              <Captioned caption={leadActs(it.id).length === 1 ? leadActs(it.id)[0].name : `Lead ${it.name}`}>
                <button type="button" title="Lead" onClick={() => { setFocus(null); setUsing(it.id); }}>🚶</button>
              </Captioned>
            )}
            {pushActs(it.id).map((a) => (
              <Captioned key={a.id} caption={a.name}>
                <button type="button" title={a.name} onClick={() => { setFocus(null); if (attempt(a)) setAwayIds((m) => [...m, it.id]); }}>🫸</button>
              </Captioned>
            ))}
          </span>
        )}
        <span className="mg-label">{it.name}</span>
      </div>
    );
  };

  // The close-up is the first registered view that fits the item (./mechanics/kinds.jsx).
  const renderCloseUp = (it) => {
    const s = st(it.id);
    const { View } = CLOSEUPS.find(it, s, views);
    return <View key={it.id} it={it} s={s} g={views} />;
  };

  // The bag (the generic core): every collectible item whose place is now "bag" - taken out of the scene or a container.
  const bag = spec.items.filter((it) => st(it.id).where === 'bag');
  // The 🎒 tray, the rooms' way: a slot for every thing to pick up from the start (empty until it's picked up), and a
  // thing put where it goes (a bin, a shelf, a person) shown used ✓. A thing made during the game gets its slot once made.
  const collectibles = spec.items.filter((it) => it.inv?.collectible && (!it.inv?.made_by || inBag(it.id)));
  const placed = Object.fromEntries(collectibles.filter((it) => !inBag(it.id) && st(it.id).where !== firstWhere.current[it.id])
    .map((it) => [it.id, true]));
  // What the end should look like (backend flows.py "summary"), each line judged on how things stand at the end.
  const summary = (spec.summary || []).map((c) => ({ ...c, right: evaluate(c.when, ctx) }));
  // In your hands: what one item gave you (the forklift's keys) that hasn't gone where it's taken yet (the key cabinet).
  const carried = spec.items.flatMap((it) => Object.values(it.inv?.actions || {})
    .filter((a) => a.gives && a.to && st(it.id).stage === a.to).map((a) => ({ what: a.gives, from: it.name })))
    .filter((c) => !spec.items.some((t) => Object.values(t.inv?.actions || {})
      .some((b) => b.takes === c.what && b.records && st(t.id)[b.records] !== null && st(t.id)[b.records] !== undefined)));
  // A step that can be undone (the door shut, then opened again) counts while its result is still in place.
  const holds = (a) => (a.effects || []).length > 0 && a.effects.every((e) => e.op === 'set' && (ctx.state[e.item] || {})[e.prop] === e.value);
  const doneRights = rights.filter((a) => ctx.done.includes(a.id) || (a.repeatable && a.interaction === 'confirm' && holds(a))).length;
  // Shift clock: game minutes from the first time the scene mentions, else elapsed shift time.
  const startAt = startMinutes([...spec.intro, ...spec.objective]);
  const shiftClock = shiftOver ? 'shift over'
    : startAt !== null ? `${clockText(startAt + clock)} → ${clockText(startAt + SHIFT_SECONDS)}` : `${shiftTime(clock)} / ${shiftTime(SHIFT_SECONDS)}`;
  const monitorTime = shiftOn ? clockText((startAt || 0) + clock) : `${clockText((startAt || 0) + Math.floor(clock / 60))}:${String(clock % 60).padStart(2, '0')}`;
  const progress = shiftOn
    ? `🕐 ${shiftClock} · ✅ ${tally.onTime} · ⏰ ${tally.late}${tally.lost ? ` · ✖ ${tally.lost}` : ''}${tally.slips ? ` · ⚠ ${tally.slips}` : ''}`
    : `👁 ${observable.filter((it) => ctx.seen.includes(it.id)).length}/${observable.length} · ✔ ${doneRights}/${rights.length}${slips.length ? ` · ✖ ${slips.length}` : ''}`;
  const groupName = (g) => byId[g.anchor]?.name || g.id;
  // What the kind/close-up plugins may use (./mechanics/kinds.jsx).
  const views = { spec, ctx, byId, asset, say, attempt, actsFor, aboutOf, partnersOf, draftChips, setDraftChips, setOpen, monitorTime, badges, pictureOf,
    take: open && isCarryable(open) && !held.includes(open) && playing ? take : null };
  const worst = plan.groups.map((g) => [g, ['late', 'lost', 'missed'].reduce((n, k) => n + (tally.byGroup[g.id]?.[k] || 0), 0)])
    .filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])[0];
  // Portaled to <body>: the page this opens from establishes a containing block for position:fixed,
  // which put the game's fixed layers (GameHeader icons, Mission Notes, the 🎒 tray) off-screen.
  return createPortal(
    <div className={`mg-backdrop ${page ? 'is-page' : ''}`} role={page ? 'main' : 'dialog'} aria-modal={page ? undefined : true}>
      <div className="mg-window">
        <header className="mg-header">
          <div>
            <h3>{chapter && <span className="mg-chapter">{chapter}</span>}{spec.mission.name}</h3>
            {!tryout && <span className="mg-objective">{spec.objective.join(' ')}</span>}
          </div>
          {spec.items.some((it) => zoneOf(it) && areaLook(it) === 'hidden') && (
            <button type="button" className={`mg-areas-toggle ${showAreas ? 'is-on' : ''}`} aria-pressed={showAreas}
              onClick={() => setShowAreas((v) => !v)}>{showAreas ? 'Hide areas' : 'Show areas'}</button>
          )}
          <span className={`mg-progress ${shiftOver ? 'is-last-call' : ''}`}>{progress}</span>
          <button type="button" className="mg-close" onClick={close} aria-label="Close">×</button>
        </header>

        {/* the stage is 3:2 (its background is): as big as fits the space under the header, never past the window */}
        <div className="mg-stage-fit">
        <div className={`mg-stage is-${phase} ${spec.items.some((it) => st(it.id).ringing) ? 'is-alarm' : ''}${walking ? ' is-walking' : ''}${showAreas ? ' areas-shown' : ''}`}
          style={{ backgroundImage: `url(${asset(viewHere ? viewHere.src : spec.background_src)})` }}
          onPointerDown={(e) => { if (e.target === e.currentTarget) { setFocus(null); setUsing(null); } }}>
          {/* a thing made during the game (the cup of coffee) is never in the scene: it appears in the bag or on its maker */}
          {spec.items.filter((it) => !it.inv?.made_by && !nestedIn(it) && inView(it)).map((it, i) => renderItem(it, i))}
          {/* where you are, and a sign to each other section - top centre: clear of the 🎒 (left) and the notes (right) */}
          {viewHere && (
            <div className="mg-wayfinding">
              <span className="mg-here">📍 {viewHere.name}</span>
              {phase === 'play' && (
                <nav className="mg-signs" aria-label="Go to another part of the work area">
                  {sections.filter((v) => v.id !== view).map((v) => (
                    <button key={v.id} type="button" className="mg-sign" onClick={() => goTo(v.id)}>→ {v.name}</button>
                  ))}
                </nav>
              )}
            </div>
          )}
          {stranger && (
            <span className={`mg-stranger is-stepping is-facing-${stranger.dir}`}
              style={{ left: `${stranger.left}%`, bottom: `${stranger.bottom}%`, transition: `left ${stranger.ms}ms linear` }}>🧍</span>
          )}
          {phase === 'play' && (
            <button type="button" className={`mg-phone ${calls.length ? 'is-ringing' : ''}`} onClick={answer}
              title={calls.length ? 'The phone is ringing' : 'Phone'}>☎️{calls.length > 1 && <small>{calls.length}</small>}</button>
          )}
          {started && (hasExit || shiftOn) && (
            <button ref={exitRef} key={pulse?.id === 'exit' ? pulse.key : 'exit'} type="button" title="Walk out"
              className={`mg-exit ${using ? 'is-target' : ''} ${pulse?.id === 'exit' ? (pulse.ok ? 'is-accepting' : 'is-refusing') : ''}`} onClick={leave}>🚪</button>
          )}

          {phase === 'scene' && (
            <div className="mg-subtitles mg-subtitles-scene">
              <SubtitleDisplay dialogues={spec.intro.map((text) => ({ text, duration: 3400 }))} onComplete={beginPlay} />
            </div>
          )}
          {phase === 'play' && subtitle.lines.length > 0 && (tryout
            ? <div key={subtitle.key} className="mg-toast" role="status">{subtitle.lines.map((l) => l.text).join(' ')}</div>
            : <div className="mg-subtitles"><SubtitleDisplay key={subtitle.key} dialogues={subtitle.lines} onComplete={() => {}} /></div>)}

          {open && byId[open] && (
            <div className="mg-overlay" onClick={() => setOpen(null)}>
              <div className="mg-closeup" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="mg-close mg-close-dark" onClick={() => setOpen(null)} aria-label="Back">×</button>
                {renderCloseUp(byId[open])}
              </div>
            </div>
          )}

          {phase === 'walk' && (
            <MissionTransition link={{ type: 'place', label: spec.mission?.name || '' }} fromSrc={spec.background_src}
              toSrc={spec.background_src} nextName={spec.mission?.name || ''} onDone={() => setPhase('brief')} />
          )}
          {phase === 'brief' && (
            <div className="mg-brief-scrim">
              <section className="mg-brief" aria-labelledby="mg-brief-title">
                <span className="mg-brief-kicker">Your task</span>
                <h2 id="mg-brief-title">{spec.mission.name}</h2>
                <p>{spec.objective.join(' ')}</p>
                {canShift && (
                  <div className="mg-modes" role="radiogroup" aria-label="How to play">
                    <button type="button" role="radio" aria-checked={!shiftOn} className={!shiftOn ? 'is-on' : ''} onClick={() => setMode('visit')}>
                      <strong>One visit</strong><small>Put this scene right</small>
                    </button>
                    <button type="button" role="radio" aria-checked={shiftOn} className={shiftOn ? 'is-on' : ''} onClick={() => setMode('shift')}>
                      <strong>Full shift</strong><small>Keep it right while things keep happening</small>
                    </button>
                  </div>
                )}
                <button type="button" className="mg-brief-begin" onClick={() => setPhase('scene')}>Begin</button>
              </section>
            </div>
          )}
          {phase === 'play' && carried.length > 0 && (
            <div className="mg-hands" aria-live="polite">🤲 {carried.map((c) => `${c.what} (${c.from})`).join(' · ')}</div>
          )}
          {using && <div className="mg-using">{isCarryable(using) || inBag(using) ? '🎒' : '🚶'} {inBag(using) ? shownName(byId[using], ctx.state) : byId[using]?.name} → ?</div>}
          {phase === 'play' && !ended && (carryable.length > 0 || collectibles.length > 0) && (
            <InventoryBox
              extraItems={[...carryable.map((it) => ({ item_id: it.id, item_name: it.name, item_image: it.src ? asset(it.src) : null, item_icon: '📦' })),
                ...collectibles.map((it) => ({ item_id: it.id, item_name: shownName(it, ctx.state), item_image: pictureOf(it) ? asset(pictureOf(it)) : null,
                  item_icon: it.inv?.icon || '📦' }))]}
              collectedItems={[...held, ...collectibles.filter((it) => inBag(it.id) || placed[it.id]).map((it) => it.id)]}
              usedItemIds={{ ...used, ...placed }}
              openItemId={using}
              counterLabel={carryable.length ? `${held.length - Object.keys(used).length}` : `${bag.length}`}
              onItemClick={(info) => {
                if (!playing) return;
                if (inBag(info.item_id) || (held.includes(info.item_id) && !used[info.item_id])) setUsing((u) => (u === info.item_id ? null : info.item_id));
              }}
            />
          )}
          {phase === 'play' && !ended && !tryout && (
            <GameHeader title={spec.mission.name} showPuzzleControls showConceptIcons={false}
              onShowHowToPlay={() => setHowToOpen(true)} onShowHint={showHint}
              availableHints={shiftOn ? 99 : 2 * (rights.length + 1)} usedHints={hintsUsed}
              notesOpen={notesOpen} onToggleNotes={() => setNotesOpen((o) => !o)} />
          )}
          <HintPanel isOpen={hintOpen} onClose={() => setHintOpen(false)} hints={hint.lines}
            revealedCount={Math.min(hintRevealed, hint.lines.length)} onRevealNext={revealNextHint} />
          <ExplanationModal isOpen={howToOpen} onClose={() => setHowToOpen(false)}
            title={`❓ How to Play: ${spec.mission.name}`} explanation={howToPlayText} />
          {phase === 'play' && !ended && !tryout && (
            <ScientistNote collapsed={!notesOpen} onToggleCollapsed={() => setNotesOpen(false)} title="📋 Mission Notes" lines={[
              ...spec.objective,
              spec.type === 'procedure' || spec.type === 'flow'
                ? '🧾 Do the job, step by step: open things to see what they tell you and what you can do. Everything is checked when you hand it over.'
                : shiftOn
                ? `⟳ You're on shift (${shiftTime(SHIFT_SECONDS)}). Things will keep going wrong - put each right as soon as it shows. ☎️ rings when one's left too long.`
                : `👁 Check every item before you finish (${observable.filter((it) => ctx.seen.includes(it.id)).length}/${observable.length}).`,
              `✋ Act on the items themselves: ${howToAct(spec.actions, helpers)}.`
            ]} />
          )}

          {ended && ended.outcome === 'shift' && (
            <div className="mg-overlay"><div className="mg-card mg-debrief">
              <h4>Shift over</h4>
              {ended.lines.map((l) => <p key={l}><em>{l}</em></p>)}
              <div className="mg-tally">
                <span><b>{tally.onTime}</b>✅ on time</span><span><b>{tally.late}</b>⏰ late</span>
                <span><b>{tally.lost}</b>✖ done wrong</span><span><b>{tally.slips}</b>⚠ slips</span><span><b>{tally.missed}</b>⏳ left open</span>
              </div>
              <table className="mg-shift-table">
                <thead><tr><th>Problem</th><th>✅</th><th>⏰</th><th>✖</th><th>⏳</th></tr></thead>
                <tbody>{plan.groups.map((g) => {
                  const r = tally.byGroup[g.id] || {};
                  return <tr key={g.id}><td>{groupName(g)}</td><td>{r.onTime || 0}</td><td>{r.late || 0}</td><td>{r.lost || 0}</td><td>{r.missed || 0}</td></tr>;
                })}</tbody>
              </table>
              {worst && <p><strong>Watch out for:</strong> {groupName(worst[0])}. {spec.actions.filter((a) => worst[0].actions.includes(a.id)).map((a) => a.name).join(' → ')}.</p>}
              <div className="mg-debrief-actions">
                <button type="button" className="mg-btn" onClick={() => restart('shift')}>Another shift</button>
                <button type="button" className="mg-btn mg-btn-quiet" onClick={() => restart('visit')}>One visit</button>
              </div>
            </div></div>
          )}
          {ended && ended.outcome !== 'shift' && (
            <div className="mg-overlay"><div className={`mg-card mg-debrief ${ended.outcome === 'fail' ? 'is-fail' : ''}`}>
              <h4>{summary.length ? `Done - ${summary.filter((c) => c.right).length} of ${summary.length} right`
                : ended.outcome === 'goal' ? 'Mission complete' : 'It went wrong'}</h4>
              {ended.lines.map((l) => <p key={l}><em>{l}</em></p>)}
              {summary.length > 0 ? (
                <ul className="mg-summary" aria-label="How it went">
                  {summary.map((c) => (
                    <li key={c.label} className={c.right ? 'is-right' : 'is-wrong'}>
                      <span aria-hidden="true">{c.right ? '✓' : '✗'}</span> {c.right ? c.label : <><strong>{c.label}</strong> - {c.mistake}</>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p><strong>You did:</strong> {spec.actions.filter((a) => ctx.done.includes(a.id)).map((a) => a.name).join(' → ') || 'nothing'}</p>
              )}
              <div className="mg-debrief-actions">
                {ended.outcome === 'goal' && onWin && (
                  <button type="button" className="mg-btn" onClick={onWin}>{winLabel ? `Continue: ${winLabel}` : 'Continue'} ›</button>
                )}
                <button type="button" className={`mg-btn ${ended.outcome === 'goal' && onWin ? 'mg-btn-quiet' : ''}`} onClick={() => restart()}>Play again</button>
                {canShift && <button type="button" className="mg-btn mg-btn-quiet" onClick={() => restart('shift')}>⟳ Try a full shift</button>}
              </div>
            </div></div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
